import { LegalBottom } from "@/components/LegalFooter";
import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  HardHat,
  ShieldCheck,
  Building2,
  Briefcase,
  Users,
  TrendingUp,
  Phone,
  Mail,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  FileText,
  Scale,
  Laptop,
  Truck,
  Upload,
  Send,
  Car,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ChatBot from "@/components/ChatBot";
import { HeaderLogo } from "@/components/AnimatedLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Social Media Links
const socialLinks = {
  instagram: "https://www.instagram.com/jyvis.assurances/",
  tiktok: "https://www.tiktok.com/@jyvis.assurances",
  youtube: "https://www.youtube.com/@JyvisAssurances",
  twitter: "https://x.com/JyvisAssurances",
  facebook: "https://www.facebook.com/profile.php?id=100078545120236",
  linkedin: "https://www.linkedin.com/in/jyvis-assurances-206017357/",
};

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  email: "contact@jyvis-assurances.com",
  address: "47 rue Jean Jaurès",
  city: "93200 Saint-Denis",
};

const CALC_ROUTES = { rc_decennale: "/devis/rc-decennale", multirisque_pro: "/devis/multirisque-pro", cybersecurite: "/devis/cybersecurite" };

const professionalProducts = [
  {
    type: "rc_decennale",
    name: "RC Décennale",
    description: "Obligatoire pour les professionnels du bâtiment, cette assurance couvre les dommages compromettant la solidité de l'ouvrage pendant 10 ans. Devis personnalisé selon votre activité.",
    icon: HardHat,
    starting_price: 76.67,
    price_label: "À partir de 920 €/an (soit 76,67 €/mois)",
    image: "https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Garantie dommages ouvrage", "Assistance juridique", "Tous corps d'état"],
  },
  {
    type: "multirisque_pro",
    name: "Multirisque Professionnelle",
    description: "Protégez vos locaux, votre matériel et votre activité contre les incendies, dégâts des eaux, vols et responsabilité civile professionnelle.",
    icon: Building2,
    starting_price: 57.5,
    price_label: "À partir de 690 €/an (soit 57,50 €/mois)",
    image: "https://images.unsplash.com/photo-1761772437826-52507ca490d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Contrat sur mesure", "Protection des données", "Perte d'exploitation"],
  },
  {
    type: "cybersecurite",
    name: "Cyber-sécurité",
    description: "Protégez votre entreprise contre les risques numériques : piratage, rançongiciel, perte de données. Assistance d'experts en cas de crise.",
    icon: ShieldCheck,
    starting_price: 100,
    image: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Veille et prévention", "Indemnisation pertes", "Gestion de crise"],
  },
  {
    type: "rc_pro",
    name: "RC Professionnelle",
    description: "Responsabilité civile pour toutes les professions : couverture des fautes professionnelles, préjudices clients et défense pénale.",
    icon: Scale,
    starting_price: 80,
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Fautes professionnelles", "Préjudices clients", "Défense pénale"],
  },
  {
    type: "flotte_auto",
    name: "Flotte Automobile",
    description: "Assurance pour vos véhicules professionnels avec tarifs dégressifs et gestion simplifiée de votre parc automobile.",
    icon: Truck,
    starting_price: 120,
    image: "https://images.unsplash.com/photo-1755728531186-4efe610d8502?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Tarifs dégressifs", "Gestion simplifiée", "Assistance 24/7"],
  },
  {
    type: "vtc",
    name: "Assurance VTC",
    description: "Assurance spécifique pour les chauffeurs VTC : RC professionnelle transport, protection juridique et garantie du véhicule. Conforme à la réglementation LOTI.",
    icon: Car,
    starting_price: 150,
    image: "https://images.unsplash.com/photo-1687634365981-f6c4e3bba317?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["RC Transport de personnes", "Protection juridique", "Bris de glace inclus"],
  },
  {
    type: "taxi",
    name: "Assurance Taxi",
    description: "Couverture complète pour les artisans taxi : responsabilité professionnelle, dommages véhicule, protection du conducteur et perte d'exploitation.",
    icon: Car,
    starting_price: 180,
    image: "https://images.unsplash.com/photo-1549884992-b48f3175d6b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["RC Pro Taxi", "Perte d'exploitation", "Protection conducteur"],
  },
  {
    type: "prevoyance_dirigeant",
    name: "Prévoyance Dirigeant",
    description: "Protection personnelle du chef d'entreprise : indemnités journalières, capital invalidité et rente pour vos proches.",
    icon: Briefcase,
    starting_price: 200,
    image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Indemnités journalières", "Capital invalidité", "Rente conjoint"],
  },
  {
    type: "sante_collective",
    name: "Santé Collective",
    description: "Complémentaire santé pour vos salariés conforme à la convention collective. Portabilité des droits et services inclus.",
    icon: Users,
    starting_price: 45,
    image: "https://images.unsplash.com/photo-1638202993928-7267aad84c31?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Conforme ANI", "Portabilité", "Services inclus"],
  },
  {
    type: "epargne_entreprise",
    name: "Épargne Salariale",
    description: "PEE, PERCO et intéressement pour fidéliser vos équipes. Avantages fiscaux pour l'entreprise et les salariés.",
    icon: TrendingUp,
    starting_price: 0,
    image: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    features: ["Avantages fiscaux", "Motivation équipes", "Gestion déléguée"],
  },
];

const sectors = [
  { name: "BTP & Construction", icon: HardHat },
  { name: "Commerce & Retail", icon: Building2 },
  { name: "Services & Conseil", icon: Briefcase },
  { name: "Tech & Digital", icon: Laptop },
  { name: "Transport & Logistique", icon: Truck },
  { name: "VTC & Taxi", icon: Car },
  { name: "Santé & Médical", icon: ShieldCheck },
];

export default function ProfessionnelsPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  
  // États pour le formulaire de devis
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [devisForm, setDevisForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    produit: "",
    message: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Scroll to category section
  const scrollToCategory = (type) => {
    setActiveCategory(type);
    const element = document.getElementById(`assurance-pro-${type}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Ouvrir la modale avec un produit présélectionné
  const openDevisModal = (productType = "") => {
    setSelectedProduct(productType);
    setDevisForm(prev => ({ ...prev, produit: productType }));
    setShowDevisModal(true);
  };

  // Gérer l'upload de fichiers
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });
    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  // Supprimer un fichier uploadé
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Soumettre le formulaire de devis
  const submitDevisForm = async (e) => {
    e.preventDefault();
    if (!devisForm.nom || !devisForm.email || !devisForm.telephone || !devisForm.produit) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("nom", devisForm.nom);
      formData.append("email", devisForm.email);
      formData.append("telephone", devisForm.telephone);
      formData.append("produit", devisForm.produit);
      formData.append("message", devisForm.message);
      formData.append("type", "professionnel");
      
      uploadedFiles.forEach((file, index) => {
        formData.append(`document_${index}`, file);
      });

      await axios.post(`${API}/devis/contact`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Votre demande a été envoyée ! Nous vous contacterons sous 24h.");
      setShowDevisModal(false);
      setDevisForm({ nom: "", email: "", telephone: "", produit: "", message: "" });
      setUploadedFiles([]);
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer ou nous appeler.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Blue */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#102692] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <HeaderLogo theme="professionnels" size="md" showName={true} dark={true} />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/professionnels" className="text-white font-semibold border-b-2 border-white pb-1">
                Professionnels
              </Link>
              <Link to="/particuliers" className="text-white/80 hover:text-white transition-colors">
                Particuliers
              </Link>
              <Link to="/espace-client">
                <Button className="rounded-full bg-white text-primary hover:bg-white/90">
                  Espace client
                </Button>
              </Link>
            </nav>

            <button className="md:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Barre de navigation des assurances pro avec défilement */}
        <div className="bg-[#0D1F75] border-t border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex overflow-x-auto scrollbar-hide gap-1 py-2 px-4">
              {professionalProducts.map((product) => {
                const Icon = product.icon;
                return (
                  <button
                    key={product.type}
                    onClick={() => scrollToCategory(product.type)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                      activeCategory === product.type
                        ? "bg-white text-[#102692]"
                        : "text-white/80 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{product.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      {/* Hero - Blue */}
      <section className="relative min-h-[70vh] flex items-center pt-32 bg-gradient-to-br from-[#102692] via-[#1E40AF] to-[#2563EB]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-6">
                <Briefcase className="w-5 h-5 text-white/80" />
                <span className="text-white/80 font-medium">Espace Professionnels</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
                Assurances sur mesure pour
                <span className="text-white/90"> votre entreprise</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                RC Décennale, multirisque, cyber-sécurité, protection des dirigeants : 
                des solutions adaptées à chaque secteur d'activité.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate("/devis/rc_decennale")}
                  className="bg-white hover:bg-white/90 text-primary rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-rc-decennale"
                >
                  Devis RC Décennale
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg"
                  onClick={() => setChatOpen(true)}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Conseil personnalisé
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {sectors.map((sector, i) => {
              const Icon = sector.icon;
              return (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-primary hover:text-white rounded-full transition-all"
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{sector.name}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-accent uppercase tracking-wider">Nos solutions pro</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Protégez votre activité professionnelle
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {professionalProducts.map((product, index) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={product.type}
                  id={`assurance-pro-${product.type}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`insurance-card group h-full transition-all duration-300 ${
                      activeCategory === product.type ? "ring-2 ring-[#102692] ring-offset-2" : ""
                    }`}
                    data-testid={`pro-product-${product.type}`}
                  >
                    <CardContent className="p-0">
                      <div className="relative h-32 overflow-hidden rounded-t-xl">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                        <div className="absolute bottom-2 left-2 right-2 flex gap-1 flex-wrap">
                          {product.features.slice(0, 2).map((f, i) => (
                            <span key={i} className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded">
                              {f}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center mb-3 -mt-10 relative z-10 shadow-lg">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                          {product.name}
                        </h3>
                        <p className="text-slate-500 text-sm mb-4 line-clamp-3">{product.description}</p>
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-primary font-semibold text-sm">
                            {product.price_label || (product.starting_price > 0 ? `Dès ${product.starting_price}€/mois` : "Sur devis")}
                          </span>
                        </div>
                        <Button
                          onClick={() => (CALC_ROUTES[product.type] ? navigate(CALC_ROUTES[product.type]) : openDevisModal(product.type))}
                          className="w-full bg-[#102692] hover:bg-[#102692]/90 text-white rounded-lg"
                          data-testid={`devis-btn-pro-${product.type}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Demander un devis
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section Formulaire de Devis */}
      <section id="devis" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-accent uppercase tracking-wider">Contact</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-4">
              Demandez votre devis personnalisé
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Remplissez le formulaire ci-dessous ou appelez-nous directement au{" "}
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#102692] font-semibold hover:underline">
                {contactInfo.phone}
              </a>. Joignez tous les documents utiles pour une étude précise.
            </p>
          </motion.div>

          <Card className="shadow-xl border-0">
            <CardContent className="p-8">
              <form onSubmit={submitDevisForm} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Nom et prénom / Raison sociale *</label>
                    <Input
                      placeholder="Votre nom ou entreprise"
                      value={devisForm.nom}
                      onChange={(e) => setDevisForm(prev => ({ ...prev, nom: e.target.value }))}
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="contact@entreprise.com"
                      value={devisForm.email}
                      onChange={(e) => setDevisForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Téléphone *</label>
                    <Input
                      type="tel"
                      placeholder="01 23 45 67 89"
                      value={devisForm.telephone}
                      onChange={(e) => setDevisForm(prev => ({ ...prev, telephone: e.target.value }))}
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Produit concerné *</label>
                    <Select
                      value={devisForm.produit}
                      onValueChange={(value) => setDevisForm(prev => ({ ...prev, produit: value }))}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Sélectionnez un produit" />
                      </SelectTrigger>
                      <SelectContent>
                        {professionalProducts.map(p => (
                          <SelectItem key={p.type} value={p.type}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Documents (PDF, JPEG, PNG - max 10MB chacun)
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#102692] hover:bg-slate-50 transition-colors"
                  >
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600">Cliquez pour ajouter vos documents</p>
                    <p className="text-sm text-slate-400 mt-1">KBIS, contrats en cours, attestations, etc.</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  {uploadedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[#102692]" />
                            <span className="text-sm text-slate-700">{file.name}</span>
                            <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Informations complémentaires</label>
                  <Textarea
                    placeholder="Décrivez votre activité, vos besoins spécifiques..."
                    value={devisForm.message}
                    onChange={(e) => setDevisForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#102692] hover:bg-[#102692]/90 text-white rounded-lg h-14 text-lg font-semibold"
                >
                  {submitting ? (
                    <>Envoi en cours...</>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer ma demande de devis
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-accent uppercase tracking-wider">Pourquoi nous choisir</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mt-3 mb-6">
                L'expertise au service des professionnels
              </h2>
              <div className="space-y-6">
                {[
                  { title: "Tarifs négociés", desc: "Accès aux meilleures conditions du marché grâce à notre volume" },
                  { title: "Accompagnement expert", desc: "Des conseillers spécialisés par secteur d'activité" },
                  { title: "Gestion simplifiée", desc: "Tous vos contrats sur un seul espace en ligne" },
                  { title: "Réactivité sinistres", desc: "Traitement prioritaire et interlocuteur dédié" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Équipe professionnelle"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-2xl shadow-xl">
                <p className="text-3xl font-bold">15+</p>
                <p className="text-sm text-white/80">ans d'expertise</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Black */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-primary font-medium mb-2">Assurer son quotidien, c'est assurer son avenir</p>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Besoin d'un audit de vos assurances ?
          </h2>
          <p className="text-white/60 mb-8">
            Notre conseiller IA analyse gratuitement vos besoins et vous propose des solutions optimisées
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setChatOpen(true)}
              className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg"
              data-testid="pro-chatbot-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Audit gratuit par IA
            </Button>
            <Button
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-8 py-6 text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              {contactInfo.phone}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Blue */}
      <footer className="bg-[#102692] border-t border-white/10 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start gap-2">
              <HeaderLogo theme="professionnels" size="md" showName={false} dark={true} />
              <p className="text-sm text-white/80 mt-2">Assurer son quotidien, c'est assurer son avenir</p>
            </div>
            <div className="flex items-center gap-6 text-white/70">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-white">
                <Phone className="w-4 h-4" /> {contactInfo.phone}
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 hover:text-white">
                <Mail className="w-4 h-4" /> {contactInfo.email}
              </a>
            </div>
          </div>
          {/* Social Links */}
          <div className="flex justify-center gap-4 mt-8">
            <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
            </a>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/50 text-sm">
            <LegalBottom />
          </div>
        </div>
      </footer>

      {/* ChatBot */}
      <ChatBot isOpen={chatOpen} onClose={() => setChatOpen(false)} userType="professionnel" />

      {/* Modale de demande de devis */}
      <AnimatePresence>
        {showDevisModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDevisModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-heading font-bold text-slate-900">Demande de devis Pro</h3>
                  <button onClick={() => setShowDevisModal(false)} className="p-2 hover:bg-slate-100 rounded-full">
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Ou appelez-nous au <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#102692] font-medium">{contactInfo.phone}</a>
                </p>
              </div>
              <form onSubmit={submitDevisForm} className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom / Raison sociale *</label>
                  <Input
                    placeholder="Votre entreprise"
                    value={devisForm.nom}
                    onChange={(e) => setDevisForm(prev => ({ ...prev, nom: e.target.value }))}
                    required
                    className="h-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Téléphone *</label>
                    <Input
                      type="tel"
                      placeholder="01 23 45 67 89"
                      value={devisForm.telephone}
                      onChange={(e) => setDevisForm(prev => ({ ...prev, telephone: e.target.value }))}
                      required
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</label>
                    <Input
                      type="email"
                      placeholder="contact@entreprise.com"
                      value={devisForm.email}
                      onChange={(e) => setDevisForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="h-11"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Produit concerné *</label>
                  <Select
                    value={devisForm.produit}
                    onValueChange={(value) => setDevisForm(prev => ({ ...prev, produit: value }))}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Sélectionnez un produit" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionalProducts.map(p => (
                        <SelectItem key={p.type} value={p.type}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Documents (optionnel)</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#102692] hover:bg-slate-50 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Cliquez pour ajouter des documents</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#102692]" />
                            <span className="text-slate-700 truncate max-w-[200px]">{file.name}</span>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Message (optionnel)</label>
                  <Textarea
                    placeholder="Décrivez votre activité..."
                    value={devisForm.message}
                    onChange={(e) => setDevisForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={3}
                    className="resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#102692] hover:bg-[#102692]/90 text-white rounded-lg h-12 font-semibold"
                >
                  {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
